import { emailRegEx } from "constants/regex";
import { ROLE_CUSTOMER, ROLE_SUPPLIER } from "constants/roles";
import fe_config from "constants/Configs";
import { getLocaleString } from "utils/localization/locale";
import { useEffect, useMemo, useRef } from "react";
import { instanceWithRetry } from "actions/axiosInstance";
import Toaster from "components/Toaster";
import { enableDEVOnly } from "handlers/features";
import TokenManager from "utils/TokenManager";
import {
  clearActiveOidcProvider,
  getActiveUserManager,
  setActiveOidcProviderFromHomeLink,
} from "utils/oidcUserManager";
import logo from "assets/images/serrala_logo.svg";
import { SSO_ALLOWED_EMAILS } from "./constants";
import {
  redirectToSsoReturnTarget,
  resolveSsoReturnTarget,
  setPingAuthorizationFlag,
  setSsoReturnTarget,
} from "./utils";
import "./style.scss";

export default function SsoLogin(props) {
  const { history, location } = props;
  const hasTriggeredRef = useRef(false);

  // Memoize email parameter extraction to avoid recalculation on every render
  const ssoEmailParam = useMemo(() => {
    const queryString = location.search.substring(1); // Remove leading ?
    const params = queryString.split("&");
    for (const param of params) {
      const [key, value] = param.split("=");
      if (key === "sso_email" && value) {
        // Decode URI component to properly handle encoded characters
        // Replace + with %2B before decoding to preserve literal + in emails
        return decodeURIComponent(value.replace(/\+/g, "%2B"));
      }
    }
    return "";
  }, [location.search]);

  const searchParams = new URLSearchParams(location.search);
  const ssoReturnUrl = searchParams.get("return_url") || "";

  useEffect(() => {
    // Prevent multiple executions
    if (hasTriggeredRef.current) return;

    //Store return_url IMMEDIATELY in localStorage before any redirects
    localStorage.setItem("home_link", ssoReturnUrl);
    setActiveOidcProviderFromHomeLink(ssoReturnUrl);

    const failAndReturn = (message) => {
      setPingAuthorizationFlag(false);
      Toaster(message, "error");
      redirectToSsoReturnTarget(history);
      localStorage.removeItem("home_link");
      clearActiveOidcProvider();
    };

    //handling basic auth user landing to this screen
    if (TokenManager.get() && localStorage.getItem("loggedInUserData")) {
      history.replace("/login");
      return;
    }

    setSsoReturnTarget(resolveSsoReturnTarget(location));

    const emailSanitized = ssoEmailParam.trim();
    const isValidEmail = emailRegEx.test(emailSanitized);

    if (!isValidEmail) {
      failAndReturn(
        getLocaleString(
          "login.invalid_sso_email",
          "Unable to start Single Sign-On. Please try again from the previous page."
        )
      );
      return;
    }

    if (
      !enableDEVOnly() &&
      !SSO_ALLOWED_EMAILS.includes(emailSanitized.toLowerCase())
    ) {
      failAndReturn(
        getLocaleString(
          "login.sso_not_allowed",
          "Single Sign-On is not available for this account."
        )
      );
      return;
    }

    // Mark as triggered to prevent duplicate calls
    hasTriggeredRef.current = true;

    instanceWithRetry
      .post("/userrole", { email: emailSanitized })
      .then(({ data = {} }) => {
        if (
          [ROLE_CUSTOMER, ROLE_SUPPLIER].includes(data.role) ||
          !data.sso_enabled
        ) {
          failAndReturn(
            getLocaleString(
              "login.sso_not_available",
              "Single Sign-On is not available for this account."
            )
          );
          return;
        }

        const isHomeLinkSet = localStorage.getItem("home_link");
        getActiveUserManager()
          .signinRedirect({
            login_hint: emailSanitized,
            post_logout_redirect_uri: isHomeLinkSet
              ? isHomeLinkSet
              : fe_config.PING_REDIRECT_URI,
          })
          .catch(() => {
            failAndReturn(
              getLocaleString(
                "login.sso_login_failed",
                "Single Sign-On failed. Please try again from the previous page."
              )
            );
          });
      })
      .catch(() => {
        failAndReturn(
          getLocaleString(
            "login.sso_login_failed",
            "Single Sign-On failed. Please try again from the previous page."
          )
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoEmailParam]);

  return (
    <div className="loginpage-container">
      <div className="form-widget">
        <div className="text-center pb-3">
          <img alt="loading" src={logo} width="150" />
        </div>
        <div className="text-center">
          <div className="spinner-border" />
          <h5 className="py-3">
            {getLocaleString(
              "login.signing_in_via_sso",
              "Signing in via SSO..."
            )}
          </h5>
        </div>
      </div>
    </div>
  );
}
